import {
  EVENT_APP_EXPERIMENTAL_SPLASH_SCREEN_READY,
  EVENT_APP_FATAL_ERROR,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_SET_LOADING_PROGRESS,
  EVENT_APP_TERMINATING,
  type OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { translate, type TranslationKey } from "@monsterverse/i18n";

import type { PCAppEventHandler } from "./PCAppEventHandler";

type LoadingStage = { str: TranslationKey; start: number };

export class SplashScreenPresentation {
  private static _instance: SplashScreenPresentation | undefined;
  static initialize(
    loadingEl: HTMLElement,
    pcAppEventHandler: PCAppEventHandler,
    onPresentationComplete: () => void
  ): SplashScreenPresentation {
    this._instance = new SplashScreenPresentation(loadingEl, pcAppEventHandler, onPresentationComplete);
    return this._instance;
  }

  // ===== PROGRESS STAGE CONFIGURATION =====
  // Defines the three-stage loading process with internationalized text
  private _progressStages: LoadingStage[] = [
    {
      str: "common.loading.loading_island",
      start: 0.0,
    },
    {
      str: "common.loading.loading_monsters",
      start: 0.55,
    },
    {
      str: "common.loading.loading_finished",
      start: 1.0,
    },
  ];

  // ===== ANIMATION STATE =====
  // Tracks progress values and timing for smooth animations
  private _displayedProgress = 0.0;
  private _actualProgress = 0.0;
  private _previousTime = 0.0;
  private _nextAnimationFrame = 0; // If set at runtime, this should be a non-zero value.

  // ===== DOM ELEMENT REFERENCES =====
  // References to progress bar components for manipulation
  private _loadingEl: HTMLElement;
  private _progressBarTextEl: HTMLElement;
  private _progressBarLeftEl: HTMLElement;
  private _progressBarMiddleEl: HTMLElement;
  private _progressBarRightEl: HTMLElement;

  constructor(
    loadingEl: HTMLElement,
    pcAppEventHandler: PCAppEventHandler,
    private readonly _onPresentationComplete: () => void
  ) {
    // ===== DOM SETUP =====
    // Initialize DOM element references and initial state
    this._loadingEl = loadingEl;
    this._progressBarTextEl = loadingEl.querySelector(".progress-bar")?.firstElementChild as HTMLElement;
    this._progressBarLeftEl = loadingEl.querySelector(".progressLeftMove") as HTMLElement;
    this._progressBarMiddleEl = loadingEl.querySelector(".progressMiddleMove") as HTMLElement;
    this._progressBarRightEl = loadingEl.querySelector(".progressRightMove") as HTMLElement;
    this.setLoadingProgress(0);
    this._resetRendering();

    // Handle NO_LOADING_SCREEN debug mode
    if (window.NO_LOADING_SCREEN) {
      this.setLoadingProgress(2);
      this._hideLoadingScreen();
    }

    // ===== EVENT HANDLER REGISTRATION =====
    // Register event handlers for application lifecycle events
    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      this.setLoadingProgress(2);
    });

    // We now sometimes expect a play button to present in some flows. This will force the progress to stage 2 (complete).
    pcAppEventHandler.on(EVENT_APP_EXPERIMENTAL_SPLASH_SCREEN_READY, () => {
      this.setLoadingProgress(2);
    });

    pcAppEventHandler.on(EVENT_APP_FATAL_ERROR, () => {
      this._cancelDraw();
      this._hideLoadingScreen();
    });

    pcAppEventHandler.on(EVENT_APP_SET_LOADING_PROGRESS, (stage: unknown) => {
      this.setLoadingProgress(stage as number);
    });

    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      const handler = data as OnAppTerminatingData;
      if (handler) {
        const { exitingMonsterverse, customizer } = handler;
        if (!exitingMonsterverse && !customizer) {
          this._show();
          this.setLoadingProgress(0);
        }
      }
    });
  }

  // ===== PROGRESS STAGE MANAGEMENT =====
  // Updates progress stage and text display
  setLoadingProgress(stage: number) {
    if (!this._progressBarTextEl) return;
    const { str, start } = this._progressStages[stage];
    this._progressBarTextEl.textContent = translate(str) || "Loading island…";
    this._actualProgress = start;
  }

  // ===== ANIMATION FRAME MANAGEMENT =====
  // Controls the animation loop for smooth progress updates
  private _requestDraw() {
    this._nextAnimationFrame = requestAnimationFrame((t) => this._draw(t));
  }

  private _cancelDraw() {
    if (this._nextAnimationFrame) cancelAnimationFrame(this._nextAnimationFrame);
  }

  private _resetRendering() {
    this._actualProgress = 0.0;
    this._displayedProgress = 0.0;
    this._previousTime = 0.0;

    this._cancelDraw();
    this._requestDraw();
  }

  // ===== UI VISIBILITY MANAGEMENT =====
  // Controls loading screen show/hide with smooth transitions
  private _show() {
    this._loadingEl.classList.remove("display-none");
    this._loadingEl.classList.remove("opacity0");

    this._resetRendering();
  }

  private _hideLoadingScreen() {
    const setDisplayNone = () => this._loadingEl.classList.add("display-none");
    const onHide = () => {
      clearTimeout(backupElementRemoval);
      setDisplayNone();
      this._loadingEl.removeEventListener("transitionend", onHide);
      this._onPresentationComplete?.();
    };

    const backupElementRemoval = setTimeout(() => setDisplayNone(), 250);
    this._loadingEl.addEventListener("transitionend", onHide);
    this._loadingEl.classList.add("opacity0");
  }

  private _hideLoadingBarAndContent() {
    const bottomContent = window.document.getElementById("application-splash-bottom-content");
    if (bottomContent) {
      bottomContent.style.display = "none";
    }

    const loadingBar = window.document.getElementById("application-splash-loading-bar");
    if (loadingBar) {
      loadingBar.style.display = "none";
    }
  }

  // ===== ANIMATION SYSTEM =====
  // Main animation loop with smooth progress interpolation
  private _draw(t: number) {
    if (!this._previousTime) {
      this._previousTime = t;
      this._requestDraw();
      return;
    }

    const delta = t - this._previousTime;
    this._previousTime = t;

    // Progress interpolation logic
    if (this._displayedProgress < this._actualProgress) {
      // Quickly advance progress bar as progress has advanced past current point
      this._displayedProgress += delta / 2000;

      // Eventually we want displayedProgress to catch up to actualProgress.
      if (this._actualProgress - this._displayedProgress < 0.001) {
        this._displayedProgress = this._actualProgress;
      }
    } else {
      // Creep progress bar slowly until progress advances to the next stage
      const nextStageStart =
        this._progressStages.find((s) => {
          return s.start > this._actualProgress;
        })?.start ?? 1;

      this._displayedProgress += this._ease(nextStageStart - this._displayedProgress) * (delta / 16000);
    }

    // Three-part progress bar rendering
    // Calculate the percentages that display on the left, center, and right sections
    // The left and right are a constant 180px width and the center fills in the difference
    // This calculation keeps the bar moving smoothly as if it was all a single image
    const sideWidth = 180;
    const leftSidePercent = sideWidth / window.innerWidth;
    const rightSidePercent = 1 - leftSidePercent;
    const sideScale = 1.0 / leftSidePercent;
    const middleScale = 1.0 / (1 - leftSidePercent * 2);

    const leftProgress = this._displayedProgress < leftSidePercent ? this._displayedProgress * sideScale : 1;
    const middleProgress =
      this._displayedProgress < leftSidePercent
        ? 0
        : this._displayedProgress > rightSidePercent
          ? 1
          : (this._displayedProgress - leftSidePercent) * middleScale;
    const rightProgress =
      this._displayedProgress < rightSidePercent ? 0 : (this._displayedProgress - rightSidePercent) * sideScale;
    this._progressBarLeftEl.style.transform = `translateX(${(-1 + leftProgress) * 100}%)`;
    this._progressBarMiddleEl.style.transform = `translateX(${(-1 + middleProgress) * 100}%)`;
    this._progressBarRightEl.style.transform = `translateX(${(-1 + rightProgress) * 100}%)`;

    // Animation loop control
    if (this._displayedProgress < 1) {
      this._requestDraw();
    } else {
      this._hideLoadingScreen();
    }
  }

  // ===== EASING FUNCTION =====
  // Quadratic ease-out for smooth animation transitions
  private _ease(x: number) {
    return 1 - (1 - x) * (1 - x); // Quadratic Ease-out
  }
}
